import { type TeamworkDocument, type TeamworkOutput, type TeamworkTeam } from './types';

export const parseTeamworkOutput = (output: string): TeamworkOutput | null => {
	const lines = output.split('\n');
	const result: TeamworkOutput = { sections: [] };
	let currentTeam: Partial<TeamworkTeam> | null = null;
	let currentDocument: Partial<TeamworkDocument> | null = null;
	let inMembersSection = false;
	let headerLines: string[] = [];
	let currentSection: {
		sectionHeader?: string;
		teams?: TeamworkTeam[];
		documents?: TeamworkDocument[];
	} = {};

	lines.forEach((line) => {
		const trimmedLine = line.trim();

		// Capture main header
		if (trimmedLine.startsWith('Here are') && !result.header && !currentTeam && !currentDocument) {
			headerLines.push(trimmedLine);
			return;
		}

		// Section headers (like "Here are the Confluence documents:")
		if (
			trimmedLine.startsWith('Here are the') &&
			trimmedLine.endsWith(':') &&
			!currentTeam &&
			!currentDocument
		) {
			// Save previous section if exists
			if (currentSection.teams || currentSection.documents) {
				result.sections.push(currentSection);
			}
			currentSection = { sectionHeader: trimmedLine.slice(0, -1) }; // Remove trailing colon
			return;
		}

		// Start of a new team
		if (trimmedLine.startsWith('- type: Team')) {
			// Save previous item
			if (currentTeam) {
				if (!currentSection.teams) {
					currentSection.teams = [];
				}
				currentSection.teams.push(currentTeam as TeamworkTeam);
			}
			if (currentDocument) {
				if (!currentSection.documents) {
					currentSection.documents = [];
				}
				currentSection.documents.push(currentDocument as TeamworkDocument);
			}
			currentTeam = { type: 'Team', members: [] };
			currentDocument = null;
			inMembersSection = false;
			return;
		}

		// Start of a new document (Confluence Page, Confluence Blog Post, etc.)
		if (trimmedLine.startsWith('- type: Confluence') || trimmedLine.startsWith('- type: Jira')) {
			// Save previous item
			if (currentTeam) {
				if (!currentSection.teams) {
					currentSection.teams = [];
				}
				currentSection.teams.push(currentTeam as TeamworkTeam);
			}
			if (currentDocument) {
				if (!currentSection.documents) {
					currentSection.documents = [];
				}
				currentSection.documents.push(currentDocument as TeamworkDocument);
			}
			currentDocument = { type: trimmedLine.substring(8).trim() };
			currentTeam = null;
			inMembersSection = false;
			return;
		}

		// Parse fields
		if (currentTeam) {
			// Team parsing
			if (inMembersSection && trimmedLine.startsWith('- [')) {
				currentTeam.members!.push(trimmedLine.substring(2).trim());
			} else if (trimmedLine.startsWith('- ')) {
				const colonIndex = trimmedLine.indexOf(':');
				if (colonIndex !== -1) {
					const fieldName = trimmedLine.substring(2, colonIndex).toLowerCase();
					const fieldValue = trimmedLine.substring(colonIndex + 1).trim();

					switch (fieldName) {
						case 'ari':
							currentTeam.ari = fieldValue;
							break;
						case 'name':
							currentTeam.name = fieldValue;
							break;
						case 'description':
							currentTeam.description = fieldValue;
							break;
						case 'creator':
							currentTeam.creator = fieldValue;
							break;
						case 'member count':
							currentTeam.memberCount = parseInt(fieldValue, 10);
							break;
						case 'teamstate':
							currentTeam.teamState = fieldValue;
							inMembersSection = false;
							break;
						case 'members':
							inMembersSection = true;
							currentTeam.members = [];
							break;
					}
				}
			}
		} else if (currentDocument && trimmedLine.startsWith('- ')) {
			// Document parsing
			const colonIndex = trimmedLine.indexOf(':');
			if (colonIndex !== -1) {
				const fieldName = trimmedLine.substring(2, colonIndex).toLowerCase();
				const fieldValue = trimmedLine.substring(colonIndex + 1).trim();

				switch (fieldName) {
					case 'ari':
						currentDocument.ari = fieldValue;
						break;
					case 'title':
						currentDocument.title = fieldValue;
						break;
					case 'url':
						currentDocument.url = fieldValue;
						break;
					case 'author':
						currentDocument.author = fieldValue;
						break;
					case 'updated':
						currentDocument.updated = fieldValue;
						break;
				}
			}
		}
	});

	// Save last item
	if (currentTeam) {
		if (!currentSection.teams) {
			currentSection.teams = [];
		}
		currentSection.teams.push(currentTeam as TeamworkTeam);
	}
	if (currentDocument) {
		if (!currentSection.documents) {
			currentSection.documents = [];
		}
		currentSection.documents.push(currentDocument as TeamworkDocument);
	}

	// Save last section
	if (currentSection.teams || currentSection.documents) {
		result.sections.push(currentSection);
	}

	if (headerLines.length > 0) {
		result.header = headerLines.join(' ');
	}

	return result.sections.length > 0 ? result : null;
};
